import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUp, Plus } from "lucide-react";
import { scrollToId, trackEvent } from "../lib/tracking";
import { GUIDES, AGE_TO_GUIDE } from "../flow/guides";
import { ANDREA_PHONE, CALENDLY_URL, contactClick } from "../lib/contact";

// Rendered only when Andrea's verified text-enabled number is configured.
const ANDREA_SMS = ANDREA_PHONE;

/* ---------- expandable topic (content stays in the DOM for crawlers) ---------- */
const Topic = ({ guideId, topic, open, onToggle }) => (
  <div id={`guide-${guideId}-${topic.id}`} className="border-t border-ink/10">
    <h3>
      <button
        data-testid={`topic-${guideId}-${topic.id}`}
        aria-expanded={open}
        aria-controls={`panel-${guideId}-${topic.id}`}
        onClick={onToggle}
        className="group w-full flex items-center justify-between gap-6 py-6 text-left"
      >
        <span className={`font-serif font-light text-xl sm:text-2xl leading-snug transition-colors duration-300 ${open ? "text-claydark" : "text-ink group-hover:text-claydark"}`}>
          {topic.q}
        </span>
        <Plus size={16} className={`shrink-0 text-smoke transition-transform duration-300 ${open ? "rotate-45 text-clay" : ""}`} />
      </button>
    </h3>
    <div
      id={`panel-${guideId}-${topic.id}`}
      role="region"
      aria-hidden={!open}
      className={`grid transition-[grid-template-rows] duration-500 ease-out ${open ? "[grid-template-rows:1fr]" : "[grid-template-rows:0fr]"}`}
    >
      <div className="overflow-hidden">
        <div className="pb-9 max-w-2xl">
          <div className="space-y-4">
            {topic.body.map((p, i) => (
              <p key={i} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
                {p}
              </p>
            ))}
          </div>
          {topic.observe && (
            <div className="pt-6">
              <p className="mb-3 text-[11px] tracking-[0.25em] uppercase font-semibold text-euca">
                What to look at
              </p>
              <div className="space-y-2.5">
                {topic.observe.map((o, i) => (
                  <p key={i} className="flex items-baseline gap-4 text-base font-normal text-ink/80">
                    <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                    {o}
                  </p>
                ))}
              </div>
            </div>
          )}
          {topic.strong && (
            <p className="pt-6 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
              {topic.strong.map((l) => (
                <span key={l} className="block">{l}</span>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  </div>
);

/* ---------- interactive check-in ---------- */
const Checklist = ({ guideId, checklist }) => {
  const [checked, setChecked] = useState({});
  const toggle = (i) => setChecked((c) => ({ ...c, [i]: !c[i] }));

  return (
    <div data-testid={`guide-checklist-${guideId}`} className="mt-16 max-w-2xl">
      <p className="font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
        {checklist.title.map((l) => (
          <span key={l} className="block">{l}</span>
        ))}
      </p>
      <div className="mt-8 border-t border-ink/10">
        {checklist.items.map((item, i) => {
          const on = !!checked[i];
          return (
            <button
              key={item}
              role="checkbox"
              aria-checked={on}
              data-testid={`check-${guideId}-${i}`}
              onClick={() => toggle(i)}
              className="group w-full flex items-baseline gap-4 py-3.5 border-b border-ink/10 text-left"
            >
              <span
                aria-hidden="true"
                className={`w-4 h-4 shrink-0 translate-y-[2px] border transition-colors duration-300 ${on ? "bg-clay border-clay" : "border-ink/30 group-hover:border-clay/60"}`}
              />
              <span className={`text-base sm:text-lg font-normal transition-colors duration-300 ${on ? "text-ink" : "text-smoke"}`}>
                {item}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-10 font-serif font-light text-ink text-2xl sm:text-3xl lg:text-4xl leading-[1.16] tracking-tight">
        {checklist.after.map((l) => (
          <span key={l.t} className={`block ${l.em ? "italic text-clay" : ""}`}>{l.t}</span>
        ))}
      </p>
      {checklist.afterNote && (
        <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
          {checklist.afterNote}
        </p>
      )}
    </div>
  );
};

/* ---------- conversion area ---------- */
const GuideCTAs = ({ guideId, ctas, onTell }) => {
  const book = ctas.find((c) => c.type === "book");
  const tell = ctas.find((c) => c.type === "tell");
  const text = ctas.find((c) => c.type === "text");
  return (
    <div className="mt-10 flex flex-col items-start gap-5">
      {book && (
        <a
          data-testid={`guide-book-${guideId}`}
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("cta_click", { location: `guide_book_${guideId}` });
            trackEvent("book_assessment_click", { location: `guide_${guideId}` });
          }}
          className="w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
        >
          {book.label}
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      )}
      {tell && (
        <button
          data-testid={`guide-cta-${guideId}`}
          onClick={() => {
            trackEvent("cta_click", { location: `guide_${guideId}`, age: guideId });
            onTell(guideId);
          }}
          className={`group w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full font-medium px-8 py-4 text-base transition-colors duration-300 ${
            book
              ? "border border-ink/25 text-ink hover:border-clay hover:text-claydark"
              : "bg-ink text-ivory hover:bg-espresso"
          }`}
        >
          {tell.label}
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      )}
      {text && ANDREA_SMS && (
        <a
          data-testid={`guide-text-${guideId}`}
          href={`sms:${ANDREA_SMS}`}
          onClick={(e) => {
            trackEvent("cta_click", { location: `guide_text_${guideId}` });
            trackEvent("text_andrea_click", { location: `guide_${guideId}` });
            contactClick("text")(e);
          }}
          className="inline-flex items-center gap-1.5 text-sm font-normal text-smoke underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-ink transition-[text-decoration-color,color] duration-300"
        >
          {text.label}
          <ArrowRight size={13} />
        </a>
      )}
    </div>
  );
};

/* ---------- extended resource blocks (rendered only when a guide supplies them) ---------- */
const BlockHeading = ({ children }) => (
  <h3 className="font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
    {children}
  </h3>
);

const DashRow = ({ children, strong }) => (
  <p className={`flex items-baseline gap-4 text-base font-normal ${strong ? "text-ink/80" : "text-smoke"}`}>
    <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
    {children}
  </p>
);

const Recognition = ({ guideId, data }) => (
  <div data-testid={`guide-recognition-${guideId}`} className="mt-16 max-w-3xl">
    <BlockHeading>{data.heading}</BlockHeading>
    <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
      {data.sub}
    </p>
    <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {data.items.map((item, i) => (
        <div
          key={item.t}
          data-testid={`guide-recognition-${guideId}-${i}`}
          className="rounded-2xl border border-ink/10 bg-creamsoft/70 p-5"
        >
          <p className="text-base font-medium text-ink leading-snug">{item.t}</p>
          <p className="mt-2 text-sm font-normal leading-relaxed text-smoke">{item.note}</p>
        </div>
      ))}
    </div>
  </div>
);

const NormalVsFoundations = ({ guideId, data }) => (
  <div data-testid={`guide-normal-vs-${guideId}`} className="mt-16 max-w-3xl">
    <BlockHeading>{data.heading}</BlockHeading>
    <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
      {data.lead}
    </p>
    <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
      <div>
        <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-dusk">
          {data.normalLabel}
        </p>
        <div className="mt-4 space-y-2.5">
          {data.normal.map((n) => (
            <DashRow key={n}>{n}</DashRow>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-euca">
          {data.foundationsLabel}
        </p>
        <div className="mt-4 space-y-2.5">
          {data.foundations.map((f) => (
            <DashRow key={f} strong>{f}</DashRow>
          ))}
        </div>
      </div>
    </div>
    <p className="mt-9 font-serif italic font-light text-xl sm:text-2xl text-clay leading-snug max-w-xl">
      {data.outro}
    </p>
  </div>
);

const AndreaQuestions = ({ guideId, data }) => (
  <div data-testid={`guide-questions-${guideId}`} className="mt-16 max-w-2xl">
    <BlockHeading>{data.heading}</BlockHeading>
    <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
      {data.lead}
    </p>
    <div className="mt-8 space-y-4">
      {data.questions.map((q) => (
        <p key={q} className="font-serif italic font-light text-xl sm:text-2xl text-smoke leading-snug">
          {q}
        </p>
      ))}
    </div>
    <p className="mt-8 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
      {data.outro}
    </p>
  </div>
);

const Tonight = ({ guideId, data }) => (
  <div data-testid={`guide-tonight-${guideId}`} className="mt-16 max-w-2xl">
    <BlockHeading>{data.heading}</BlockHeading>
    <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
      {data.lead}
    </p>
    <div className="mt-9 border-t border-ink/10">
      {data.items.map((item, i) => (
        <div key={item.t} data-testid={`guide-tonight-${guideId}-${i}`} className="py-5 border-b border-ink/10">
          <p className="text-base sm:text-lg font-normal text-ink leading-snug">
            <span aria-hidden="true" className="font-serif italic text-clay mr-3">
              {String(i + 1).padStart(2, "0")}
            </span>
            {item.t}
          </p>
          <p className="mt-2 pl-10 text-sm sm:text-base font-normal leading-relaxed text-smoke">
            {item.note}
          </p>
        </div>
      ))}
    </div>
    <p className="mt-7 text-sm sm:text-base font-normal leading-relaxed text-smoke max-w-xl">
      {data.note}
    </p>
  </div>
);

const WhenSupport = ({ guideId, data }) => (
  <div data-testid={`guide-when-support-${guideId}`} className="mt-16 max-w-2xl">
    <BlockHeading>{data.heading}</BlockHeading>
    <div className="mt-5 space-y-5 max-w-xl">
      {data.paras.map((p, i) => (
        <p key={i} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
          {p}
        </p>
      ))}
    </div>
    <div className="mt-8 space-y-2.5">
      {data.points.map((p) => (
        <DashRow key={p} strong>{p}</DashRow>
      ))}
    </div>
    <p className="mt-9 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug max-w-xl">
      {data.closing}
    </p>
  </div>
);

/* ---------- the guide ---------- */
export const Response = ({ visitorAge, onSelect, onStart }) => {
  const reduce = useReducedMotion();
  const guide = visitorAge ? GUIDES[AGE_TO_GUIDE[visitorAge]] : null;
  const [openTopic, setOpenTopic] = useState(null);

  const validOpen =
    guide && guide.topics.some((t) => t.id === openTopic)
      ? openTopic
      : openTopic === "__none"
        ? null
        : guide
          ? guide.topics[0].id
          : null;

  return (
    <section id="your-stage" data-testid="your-stage-section" className="bg-ivory overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 min-h-[70svh]">
        <AnimatePresence mode="wait">
          {!guide ? (
            <motion.div
              key="empty"
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="max-w-3xl flex flex-col justify-center min-h-[40svh]"
              data-testid="your-stage-empty"
            >
              <h2 className="font-serif font-light text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
                Every stage is different.
                <span className="block italic text-clay mt-2">The support should be too.</span>
              </h2>
              <button
                data-testid="your-stage-invite"
                onClick={() => scrollToId("sleep-help-by-age")}
                className="mt-10 self-start group inline-flex items-center gap-2.5 text-sm font-normal text-smoke hover:text-claydark transition-colors duration-300"
              >
                <ArrowUp size={14} className="text-clay transition-transform duration-300 group-hover:-translate-y-0.5" />
                Choose your child&rsquo;s stage
              </button>
            </motion.div>
          ) : (
            <motion.div
              key={guide.num}
              initial={reduce ? false : { opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              data-testid="your-stage-content"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
                {/* Field-guide rail */}
                <div className="lg:col-span-3 flex lg:flex-col items-center lg:items-start gap-8">
                  <div className="lg:sticky lg:top-28">
                    <p aria-hidden="true" className="font-serif font-light leading-none text-clay/40 select-none text-7xl lg:text-[7.5rem]">
                      {guide.num}
                    </p>
                    <p className="mt-3 text-xs tracking-[0.3em] uppercase font-semibold text-euca">
                      {guide.name}
                    </p>
                    <p className="mt-1 text-sm font-normal text-smoke">{guide.unit}</p>
                    <img
                      src={guide.img}
                      alt={guide.alt}
                      loading="lazy"
                      className="hidden lg:block w-40 aspect-[4/5] object-cover rounded-[1.5rem] shadow-[0_20px_40px_-20px_rgba(42,33,24,0.3)] mt-10"
                    />
                  </div>
                </div>

                {/* The guide itself */}
                <div className="lg:col-span-8 lg:col-start-5">
                  {guide.resourceTitle ? (
                    <>
                      <motion.h2
                        initial={reduce ? false : { opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
                        data-testid={`guide-resource-title-${guide.id}`}
                        className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.14] tracking-tight"
                      >
                        {guide.resourceTitle}
                      </motion.h2>
                      <p data-testid="guide-statement" className="mt-4 font-serif font-light text-ink text-2xl sm:text-[1.75rem] leading-[1.22] tracking-tight">
                        {guide.titleLines.map((l, i) => (
                          <motion.span
                            key={l.t}
                            initial={reduce ? false : { opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.32 + i * 0.14, ease: "easeOut" }}
                            className={`block ${l.em ? "italic text-clay" : ""}`}
                          >
                            {l.t}
                          </motion.span>
                        ))}
                      </p>
                      {guide.resourceDescriptor && (
                        <motion.p
                          initial={reduce ? false : { opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.7, delay: 0.58, ease: "easeOut" }}
                          className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl"
                        >
                          {guide.resourceDescriptor}
                        </motion.p>
                      )}
                    </>
                  ) : (
                  <h2 data-testid="guide-statement" className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.14] tracking-tight">
                    {guide.titleLines.map((l, i) => (
                      <motion.span
                        key={l.t}
                        initial={reduce ? false : { opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 + i * 0.14, ease: "easeOut" }}
                        className={`block ${l.em ? "italic text-clay" : ""}`}
                      >
                        {l.t}
                      </motion.span>
                    ))}
                  </h2>
                  )}

                  {guide.introFragments && (
                    <div className="mt-10 space-y-3.5 max-w-xl">
                      {guide.introFragments.map((r, i) => (
                        <motion.p
                          key={r}
                          initial={reduce ? false : { opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.55, delay: 0.6 + i * 0.12, ease: "easeOut" }}
                          className="flex items-baseline gap-4 text-lg sm:text-xl font-normal text-smoke"
                        >
                          <span aria-hidden="true" className="h-px w-6 shrink-0 bg-clay translate-y-[-6px]" />
                          {r}
                        </motion.p>
                      ))}
                    </div>
                  )}

                  {guide.introParas && (
                    <div className="mt-8 max-w-xl space-y-5">
                      {guide.introParas.map((s, i) => (
                        <p key={i} className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
                          {s}
                        </p>
                      ))}
                    </div>
                  )}

                  {guide.introStrong && (
                    <p className="mt-8 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug max-w-xl">
                      {guide.introStrong.map((l) => (
                        <span key={l.t} className={`block ${l.em ? "italic text-clay" : ""}`}>{l.t}</span>
                      ))}
                    </p>
                  )}

                  {guide.introTurn && (
                    <div className="mt-10 max-w-xl">
                      <p className="text-sm font-normal text-smoke">{guide.introTurn.lead}</p>
                      <p className="mt-4 font-serif italic font-light text-xl sm:text-2xl text-smoke leading-snug">
                        &ldquo;{guide.introTurn.from}&rdquo;
                      </p>
                      <p className="mt-4 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
                        to: <span className="italic text-clay">&ldquo;{guide.introTurn.to}&rdquo;</span>
                      </p>
                    </div>
                  )}

                  {guide.introAfter && (
                    <p className="mt-8 font-serif italic font-light text-2xl sm:text-3xl text-clay leading-snug max-w-xl">
                      {guide.introAfter}
                    </p>
                  )}

                  {guide.recognition && <Recognition guideId={guide.id} data={guide.recognition} />}
                  {guide.normalVsFoundations && <NormalVsFoundations guideId={guide.id} data={guide.normalVsFoundations} />}
                  {guide.andreaQuestions && <AndreaQuestions guideId={guide.id} data={guide.andreaQuestions} />}
                  {guide.tonight && <Tonight guideId={guide.id} data={guide.tonight} />}
                  {guide.whenSupport && <WhenSupport guideId={guide.id} data={guide.whenSupport} />}

                  {/* Explore this stage */}
                  <div className="mt-16">
                    <p className="mb-2 text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                      Explore this stage
                    </p>
                    <div className="border-b border-ink/10">
                      {guide.topics.map((topic) => (
                        <Topic
                          key={topic.id}
                          guideId={guide.id}
                          topic={topic}
                          open={validOpen === topic.id}
                          onToggle={() => setOpenTopic(validOpen === topic.id ? "__none" : topic.id)}
                        />
                      ))}
                    </div>
                  </div>

                  <Checklist guideId={guide.id} checklist={guide.checklist} />

                  {/* Ending + conversion */}
                  <div data-testid={`guide-conversion-${guide.id}`} className="mt-20 lg:mt-24 max-w-2xl border-t border-ink/10 pt-14">
                    <p data-testid={`guide-ending-${guide.id}`} className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.12] tracking-tight">
                      {guide.ending.lines.map((l) => (
                        <span key={l.t} className={`block ${l.em ? "italic text-clay" : ""}`}>{l.t}</span>
                      ))}
                    </p>
                    {guide.ending.sub && (
                      <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
                        {guide.ending.sub}
                      </p>
                    )}
                    <GuideCTAs guideId={guide.id} ctas={guide.ctas} onTell={onStart} />
                    {guide.deepLink && (
                      <a
                        data-testid={`guide-deep-link-${guide.id}`}
                        href={guide.deepLink.href}
                        className="mt-7 inline-block text-sm font-normal text-smoke underline decoration-clay/50 underline-offset-8 hover:text-ink transition-colors duration-300"
                      >
                        {guide.deepLink.label} →
                      </a>
                    )}
                  </div>

                  <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="text-xs tracking-[0.25em] uppercase font-semibold text-dusk">
                      A different stage?
                    </span>
                    {Object.values(GUIDES)
                      .filter((g) => g.id !== guide.id)
                      .map((g) => (
                        <button
                          key={g.id}
                          data-testid={`stage-switch-${g.id}`}
                          onClick={() => {
                            setOpenTopic(null);
                            onSelect(g.id);
                          }}
                          className="text-sm font-normal text-smoke underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-ink transition-[text-decoration-color,color] duration-300"
                        >
                          {g.name}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
